import { Component, EventEmitter, inject, input, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { InitialAssessmentForm } from '../../data/initial-assessment-form';
import { MatTableDataSource } from '@angular/material/table';
import { EmployerSponsorDetails } from '../../models/employer-sponsor-details';
import { PositionsModel } from '../../models/positions';
import { PositionDialogComponent } from '../../components/position-dialog/position-dialog.component';
import { LookupItem } from '../../models/lookup-item';
import { IFormTypes } from '../../data/form-types';
import { IFormComponent } from '../../models/i-form-component';
import { requiredListWhenYesValidator } from '../../validators/required-list-when-yes-validator';
import { map, Subject, takeUntil } from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';

@Component({
  selector: 'app-positions',
  templateUrl: './positions.component.html',
  styleUrls: ['./positions.component.scss']
})
export class PositionsComponent implements OnInit, IFormComponent {

  formBuilder = inject(NonNullableFormBuilder);
  dialog = inject(MatDialog);
  
  @Input() model!: InitialAssessmentForm;
  @Input() educationLevels!: LookupItem[];
  @Input() countries!: LookupItem[];
  @Input() titles!: LookupItem[];
  @Input() maritalStatus!: LookupItem[];
  @Output() formReady = new EventEmitter<FormGroup>();

  displayedColumns = ['jobTitle', 'salary', 'applicant', 'edit', 'delete'];
  positionsDataSource = new MatTableDataSource<PositionsModel>([]);
  submitAttempted: boolean = false;

  // Maps to EmployerSponsorDetails
  positionsForm = this.formBuilder.group({
    knowPositions: ['', Validators.required]
  },{
    validators: requiredListWhenYesValidator([
                    {
                      controlName: 'knowPositions',
                      errorKey: 'positionsRequired',
                      getItems: () => this.model?.employerSponsor?.positions ?? []
                    }])
  });

  isFormValid(): boolean {
    this.submitAttempted = true;
    return this.positionsForm.valid;
  }

  markFormAsTouched(): void {
    this.positionsForm.markAllAsTouched();
  }

  private destroy$ = new Subject<void>();
  private breakpointObserver = inject(BreakpointObserver);
  isMobile$ = this.breakpointObserver.observe(['(max-width: 768px)']).pipe(
      map(result => result.matches),
      takeUntil(this.destroy$)
  );
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {

    this.model = this.model || new InitialAssessmentForm();
    this.model.employerSponsor = this.model.employerSponsor || new EmployerSponsorDetails();
    this.positionsDataSource.data = this.model.employerSponsor!.positions;

    // Bind form to model data
    this.positionsForm.patchValue(this.model.employerSponsor);

    // Subscribe to form changes to update model
    this.positionsForm.valueChanges.subscribe(value => {
      this.model.employerSponsor = {
        ...this.model.employerSponsor!,
        ...value
      }
    });
  }

  refreshpositionsTable(): void {
    this.positionsDataSource.data = [...this.model.employerSponsor!.positions];
  }

  add(): void {
    const dialogRef = this.dialog.open(PositionDialogComponent, {
      panelClass: 'dialog-panel-large',
      maxHeight: 'none',
      data: { 
        position: null,
        educationLevels: this.educationLevels,
        countries: this.countries,
        titles: this.titles,
        maritalStatus: this.maritalStatus
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.model.employerSponsor!.positions.push(result);
        this.refreshpositionsTable();
        this.positionsForm.controls.knowPositions.updateValueAndValidity();
      }
    });
  }

  remove(index: number): void {
    this.model.employerSponsor!.positions.splice(index, 1);
    this.refreshpositionsTable();
    this.positionsForm.controls.knowPositions.updateValueAndValidity();
  }

  edit(index: number): void {
    const position = this.model.employerSponsor!.positions[index];
    const dialogRef = this.dialog.open(PositionDialogComponent, {
      panelClass: 'dialog-panel-large',
      maxHeight: 'none',
      data: {
        position: position, 
        educationLevels: this.educationLevels, 
        countries: this.countries,
        titles: this.titles,
        maritalStatus: this.maritalStatus
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.model.employerSponsor!.positions[index] = result;
        this.refreshpositionsTable();
        this.positionsForm.controls.knowPositions.updateValueAndValidity();
      }
    });
  }

  getApplicantName = (position: PositionsModel) => {
    if (!position || !position.applicantKnown) {
      return '';
    }

    let applicantName = `${position.firstName || ''} ${position.surname || ''}`.trim();
    if (position.title) {
      let title = position.title.id === 'Other'
        ? position.titleOther
        : position.title.value;
      return `${title} ${applicantName}`.trim();
    }
    return applicantName;
  }

}
